import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { CompetitiveInsightsCountyFilterTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsCountyFilterType";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsTabTypeEnum";
import { QESButton } from "@app/shared/qes-standards/Button";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import MedicaidNetworkFlyout from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidNetworkFlyout";
import QuestProprietaryDataIcon from "@app/overviews/network-benchmarking-dashboard/filters/QuestProprietaryDataIcon";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";

interface MedicaidNetworkFiltersTableProps {
  colorMap?: Map<string, string>;
  data: MedicaidNetwork[];
  isMapView?: boolean;
  isNetworkSummary?: boolean;
  parentOrganizationName: string;
  selectedContractIds: string[];
}

interface MedicaidNetworkFiltersTableData {
  contractName: JSX.Element;
  countyCount: string;
  membership: string;
  opportunityCountyCount: string;
  rowIsDisabled?: boolean;
  [wafflesKeyColumnName]: string;
}

const MedicaidNetworkFiltersTable = (props: MedicaidNetworkFiltersTableProps): JSX.Element => {
  const [tableData, setTableData] = useState<MedicaidNetworkFiltersTableData[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const handleFlyoutOpen = (): void => {
    if (benchmarkingFiltersContext.selectedTab === CompetitiveInsightsTabTypeEnum.NetworkSelection) {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  useEffect(() => {
    if (!!props.data) {
      /* oxlint-disable sort-keys */
      const newTableData: MedicaidNetworkFiltersTableData[] = props.data.map((contract, index) => {
        return {
          [wafflesKeyColumnName]: contract.contractId || index.toString(),
          contractName: (
            <>
              <FlyoutLink
                classNames="qes-button qes-button-secondary h-contract-flyout-link-btn"
                testId={`network-benchmarking-dashboard--network-filters__flyout-link-${contract.contractId}`}
                onOpen={handleFlyoutOpen}
                content={{
                  body: (
                    <MedicaidNetworkFlyout
                      contract={contract}
                    />
                  ),
                  title: "",
                }}
              >
                {props.isNetworkSummary && (
                  <QesIcon
                    style={{ color: props.colorMap?.get(contract.contractId), fill: props.colorMap?.get(contract.contractId) }}
                    iconName={FontAwesome.Square}
                    width="25"
                    className="me-3"
                    description="Open network flyout"
                  />
                )}
                <span className="h-contract-flyout-link">
                  {props.isNetworkSummary ? `${contract.parentOrganization} - ${contract.program}` : contract.program}
                </span>
              </FlyoutLink>
              {!contract.disabled && (
                <QuestProprietaryDataIcon />
              )}
            </>
          ),
          membership: `~${printNumberAsString(contract.membershipCount, "---")}`,
          countyCount: printNumberAsString(contract.countyCount, "---"),
          opportunityCountyCount: printNumberAsString(contract.opportunityCountyCount, "---"),
          // disable row if contract is disabled (due to lack of data) OR
          // five contracts have already been selected and the row isn't one of the selected h contracts
          rowIsDisabled: contract.disabled || (
            benchmarkingFiltersContext.selectedNetworks.length === 5 && !benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId).includes(contract.contractId)
          ),
        }
      });
      /* oxlint-enable sort-keys */

      setTableData(newTableData);
    }
  }, [props.data, props.colorMap, props.isNetworkSummary, benchmarkingFiltersContext.selectedNetworks]);

  const columnConfig: ColumnConfig = {
    contractName: {
      alignment: TextAlignment.Left,
      displayText: "",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "contract-name",
    },
    countyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>{props.parentOrganizationName === "Your QES Networks" ? "Current" : "Active"}<br />County Count</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "county-count",
    },
    membership: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Membership</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "membership",
    },
    opportunityCountyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>Opportunity<br />County Count</>,
      hidden: props.parentOrganizationName !== "Your QES Networks",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "opportunity-county-count",
    },
  };

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    // obtain the h contract models based off their contract ids
    const selectedHContracts: MedicaidNetwork[] = cloneDeep(props.data).filter(
      (x) => selectedKeys.includes(x.contractId.toString())
    );

    // since toggling a waffles checkbox returns all of the currently-selected keys
    // and the selected h contracts array can include h contracts from different parent organizations,
    // filter out all h contracts that correspond to this parent organization from the select h contracts...
    const newSelectedHContracts: MedicaidNetwork[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks as MedicaidNetwork[]).filter(
      (x) => !props.data.map(d => d.contractId).includes(x.contractId)
    );

    // ...and then add back the selected h contracts for this parent organization
    benchmarkingFiltersContext.setSelectedNetworks(newSelectedHContracts.concat(selectedHContracts));
  }

  const onRowMouseOver = (row: MedicaidNetworkFiltersTableData): void => {
    if (!!row) {
      const network: MedicaidNetwork = props.data.find(x => x.contractId === row[wafflesKeyColumnName]);

      if (!!network) {
        benchmarkingFiltersContext.setMousedOverCountyIds(network.networkCounties.map(nc => nc.countyId.toString()));
        benchmarkingFiltersContext.setMousedOverNetwork(network);
      }
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
      benchmarkingFiltersContext.setMousedOverNetwork(null);
    }
  }

  return (
    <div className="mb-3">
      {props.parentOrganizationName === "Your QES Networks" && props.isMapView && (
        <div className="d-flex flex-wrap">
          {Object.values(CompetitiveInsightsCountyFilterTypeEnum).map((filterType, index) => (
            <QESButton
              key={index}
              qesTestId={`network-benchmarking-dashboard__network-filters--county-chip-${filterType.toLowerCase()}`}
              qesType="none"
              className={`county-filter-type-pill me-2${benchmarkingFiltersContext.selectedCountyFilterType.includes(filterType) ? " county-filter-type-pill--active" : ""}`}
              onClick={() => benchmarkingFiltersContext.toggleSelectedCountyFilterType(filterType)}
            >
              {filterType}
            </QESButton>
          ))}
        </div>
      )}
      <Waffles<MedicaidNetworkFiltersTableData>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        disableProcessingOverlay={true}
        fullWidth={true}
        handleCheckboxChange={handleCheckboxChange}
        keyColumns={["contractName"]}
        onRowMouseOver={onRowMouseOver}
        selectedKeys={benchmarkingFiltersContext.selectedNetworks.map((x) => x.contractId.toString())}
        showCheckboxes={!props.isNetworkSummary}
        testId={`network-benchmarking-dashboard--network-filters__table-${props.parentOrganizationName?.replace(/\s+/g, '-').toLowerCase()}`}
        theme={WafflesThemes.Simple}
      />
    </div>

  )
}

export default MedicaidNetworkFiltersTable;
